import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Truck, CheckCircle2 } from "lucide-react";

// Optional post-save step: send purchased SKUs from the HO warehouse straight on to one or more showrooms.
export function VpDispatchSection({ purchase, onDone }) {
  const [info, setInfo] = useState(null);
  const [targets, setTargets] = useState([]);
  const [autoReceive, setAutoReceive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);

  const load = async () => {
    try { const { data } = await api.get(`/vendor-purchases/${purchase.id}/dispatchable`); setInfo(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load dispatch info"); }
  };
  useEffect(() => { load(); }, [purchase?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const lines = info?.lines || [];
  const used = useMemo(() => {
    const m = {};
    targets.forEach((t) => Object.entries(t.items).forEach(([pid, q]) => { m[pid] = (m[pid] || 0) + (Number(q) || 0); }));
    return m;
  }, [targets]);
  const remaining = (pid) => { const l = lines.find((x) => x.product_id === pid); return Math.max((l?.available_qty || 0) - (used[pid] || 0), 0); };
  const freeShowrooms = (info?.showrooms || []).filter((s) => !targets.some((t) => t.to_branch_id === s.id));

  const addTarget = (bid) => { if (!bid) return; setTargets((t) => [...t, { to_branch_id: bid, items: {} }]); };
  const setQty = (ti, pid, v) => setTargets((t) => t.map((x, i) => i === ti ? { ...x, items: { ...x.items, [pid]: v } } : x));
  const removeTarget = (ti) => setTargets((t) => t.filter((_, i) => i !== ti));
  const over = Object.entries(used).some(([pid, q]) => { const l = lines.find((x) => x.product_id === pid); return q > (l?.available_qty || 0) + 0.0001; });
  const payloadTargets = targets.map((t) => ({ to_branch_id: t.to_branch_id, items: Object.entries(t.items).filter(([, q]) => Number(q) > 0).map(([product_id, q]) => ({ product_id, quantity: Number(q) })) })).filter((t) => t.items.length);
  const canConfirm = payloadTargets.length > 0 && payloadTargets.length === targets.length && !over && !busy;

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/vendor-purchases/${purchase.id}/dispatch`, { dispatches: payloadTargets, auto_receive: autoReceive });
      setResults(data.dispatches || []);
      setTargets([]);
      setInfo((i) => ({ ...i, lines: data.lines, direct_dispatches: data.purchase?.direct_dispatches || [...(i.direct_dispatches || []), ...(data.dispatches || [])] }));
      toast.success(`${data.dispatches.length} showroom dispatch${data.dispatches.length === 1 ? "" : "es"} created`);
      onDone?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Dispatch failed"); }
    finally { setBusy(false); }
  };

  if (!info) return <div className="text-xs text-[#8B7355] flex items-center gap-2" data-testid="vp-dispatch-loading"><Loader2 className="w-3 h-3 animate-spin" /> Loading dispatch options…</div>;
  const name = (pid) => lines.find((x) => x.product_id === pid)?.name || pid;
  return (
    <div className="rounded-lg border border-[#EAE5D9] bg-[#FBF8F2] p-4 space-y-4 min-w-0 max-w-full" data-testid="vp-dispatch-section">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-[Outfit] font-semibold text-[#2C1810] flex items-center gap-2"><Truck className="w-4 h-4 text-[#C05621]" /> Dispatch to Showroom <span className="text-xs font-normal text-[#8B7355]">(optional)</span></div>
          <p className="text-xs text-[#8B7355] mt-0.5">Stock is already at {info.from_branch_name}. Pick showrooms and quantities — one dispatch + one GRN per showroom. Anything you don't send stays at the HO warehouse.</p>
        </div>
      </div>

      <div className="overflow-x-auto max-w-full">
        <table className="w-full text-xs" data-testid="vp-dispatch-lines">
          <thead className="uc-label text-[#8B7355]"><tr><th className="text-left py-1">Purchased SKU</th><th className="text-right py-1">Bought</th><th className="text-right py-1">At HO</th><th className="text-right py-1">Left to send</th></tr></thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.product_id} className="border-t border-[#EAE5D9]" data-testid={`vp-dispatch-line-${l.product_id}`}>
                <td className="py-1.5">{l.name} <span className="text-[#8B7355]">{l.sku}</span></td>
                <td className="py-1.5 text-right">{l.quantity} {l.unit}</td>
                <td className="py-1.5 text-right">{l.available_qty}</td>
                <td className={`py-1.5 text-right font-semibold ${remaining(l.product_id) <= 0 ? "text-[#8B7355]" : "text-[#C05621]"}`} data-testid={`vp-dispatch-remaining-${l.product_id}`}>{remaining(l.product_id)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {targets.map((t, ti) => {
        const s = (info.showrooms || []).find((x) => x.id === t.to_branch_id) || {};
        return (
          <div key={t.to_branch_id} className="rounded-md border border-[#EAE5D9] bg-white p-3 space-y-2" data-testid={`vp-dispatch-target-${t.to_branch_id}`}>
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-[#2C1810]">{s.name} <span className="text-xs font-normal text-[#8B7355]">{s.code}</span></div>
              <button onClick={() => removeTarget(ti)} className="p-1 text-[#8B7355] hover:text-red-600" data-testid={`vp-dispatch-remove-${t.to_branch_id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {lines.map((l) => {
                const max = (l.available_qty || 0) - ((used[l.product_id] || 0) - (Number(t.items[l.product_id]) || 0));
                return (
                  <div key={l.product_id} className="flex items-center justify-between gap-2 text-xs">
                    <span className="truncate">{l.name}</span>
                    <div className="flex items-center gap-1 shrink-0">
                      <input type="number" min="0" step="any" value={t.items[l.product_id] ?? ""} placeholder="0"
                        onChange={(e) => setQty(ti, l.product_id, e.target.value)}
                        className={`w-20 h-8 border rounded px-2 text-right ${Number(t.items[l.product_id]) > max + 0.0001 ? "border-red-400 bg-red-50" : "border-[#EAE5D9]"}`}
                        data-testid={`vp-dispatch-qty-${t.to_branch_id}-${l.product_id}`} />
                      <button type="button" onClick={() => setQty(ti, l.product_id, Math.max(max, 0))} className="text-[10px] text-[#C05621] hover:underline" data-testid={`vp-dispatch-all-${t.to_branch_id}-${l.product_id}`}>all</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <div className="flex flex-wrap items-center gap-3">
        <select className="h-9 border border-[#EAE5D9] rounded-md px-2 text-sm bg-white" value="" onChange={(e) => addTarget(e.target.value)} disabled={!freeShowrooms.length} data-testid="vp-dispatch-add-showroom">
          <option value="">{freeShowrooms.length ? "+ Add showroom…" : "All showrooms added"}</option>
          {freeShowrooms.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}
        </select>
        <label className="flex items-center gap-2 text-xs text-[#5C4033] cursor-pointer">
          <input type="checkbox" checked={autoReceive} onChange={(e) => setAutoReceive(e.target.checked)} data-testid="vp-dispatch-auto-receive" />
          Mark received at showroom immediately (goods go straight there)
        </label>
        <div className="flex-1" />
        <Button onClick={confirm} disabled={!canConfirm} className="bg-[#2C1810] hover:bg-[#1a0e09] text-white" data-testid="vp-dispatch-confirm-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Confirm dispatch{payloadTargets.length > 1 ? `es (${payloadTargets.length})` : ""}</>}
        </Button>
      </div>
      {over && <div className="text-xs text-red-600" data-testid="vp-dispatch-over-error">A quantity exceeds what is left of this purchase at the HO warehouse.</div>}
      {!autoReceive && targets.length > 0 && <div className="text-xs text-[#8B7355]">The GRN will wait in each showroom's Goods Received → Inbound list for them to confirm.</div>}

      {(info.direct_dispatches || []).length > 0 && (
        <div className="space-y-1" data-testid="vp-dispatch-results">
          <Label className="uc-label">Dispatched from this purchase</Label>
          {info.direct_dispatches.map((r) => {
            const isNew = results.some((x) => x.dispatch_id === r.dispatch_id);
            return (
            <div key={r.dispatch_id} className={`flex flex-wrap items-center gap-2 text-xs bg-white border rounded px-2 py-1.5 ${isNew ? "border-emerald-400" : "border-[#EAE5D9]"}`} data-testid={`vp-dispatch-result-${r.dispatch_id}`}>
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
              <span className="font-semibold">{r.to_branch_name}</span>
              <Badge variant="outline" className="font-mono">{r.dispatch_number}</Badge>
              <Badge variant="outline" className="font-mono">{r.grn_number}</Badge>
              <Badge className={r.auto_received ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"}>{r.auto_received ? "Received" : "Awaiting GRN"}</Badge>
              {isNew && <Badge className="bg-[#C05621] text-white">New</Badge>}
              <span className="text-[#5C4033]">{(r.items || []).map((i) => `${i.name || name(i.product_id)} × ${i.quantity}`).join(", ")}</span>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
